namespace OSF {
  export enum OsfNavigationMode {
    DefaultMode = 0,
    CategoryMode = 1,
    TrustPageMode = 2,
    QueryResultMode = 3,
  }
  export let ManifestNamespaces = {
    "1.0":
      'xmlns="http://schemas.microsoft.com/office/appforoffice/1.0" xmlns:o="http://schemas.microsoft.com/office/appforoffice/1.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"',
    "1.1":
      'xmlns="http://schemas.microsoft.com/office/appforoffice/1.1" xmlns:o="http://schemas.microsoft.com/office/appforoffice/1.1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bt="http://schemas.microsoft.com/office/officeappbasictypes/1.0" xmlns:ov="http://schemas.microsoft.com/office/taskpaneappversionoverrides" xmlns:c="http://schemas.microsoft.com/office/contentappversionoverrides" xmlns:mailappor="http://schemas.microsoft.com/office/mailappversionoverrides"',
  };
  export let ManifestSchemaVersion = {
    "1.0": "1.0",
    "1.1": "1.1",
  };
  export class RequirementsChecker {
    _filteringEnabled: any;
    _supportedCapabilities: any;
    _supportedControlTargets: any;
    _supportedHosts: any;
    _supportedMethods: any;
    _supportedOmexAppVersions: any;
    _supportedSets: any;
    defaultMinMaxVersion: string = "1.1";
    constructor(
      supportedCapabilities,
      supportedHosts,
      supportedRequirements,
      supportedControlTargets,
      supportedOmexAppVersions,
    ) {
      this.setCapabilities(supportedCapabilities);
      this.setHosts(supportedHosts);
      this.setRequirements(supportedRequirements);
      this.setSupportedControlTargets(supportedControlTargets);
      this.setSupportedOmexAppVersions(supportedOmexAppVersions);
      // Filtering is disabled by default
      this.setFilteringEnabled(false);
    }

    isManifestSupported(manifest) {
      if (!this.isFilteringEnabled()) {
        return true;
      }
      if (!manifest) {
        return false;
      }
      var manifestSchemaVersion = manifest.getManifestSchemaVersion() || OSF.ManifestSchemaVersion["1.0"];
      switch (manifestSchemaVersion) {
        case OSF.ManifestSchemaVersion["1.0"]:
          return this._checkManifest1_0(manifest);
        case OSF.ManifestSchemaVersion["1.1"]:
          return this._checkManifest1_1(manifest);
        default:
          // Unknown manifest version
          // Shouldn't happen, but for consistency
          return false;
      }
    }

    isEntitlementFromOmexSupported(entitlement: OfficeExt.AppDetail) {
      try {
        if (!this.isFilteringEnabled()) {
          return true;
        }
        if (!entitlement) {
          return false;
        }
        var targetType;
        switch (entitlement.appSubType) {
          case "1":
            targetType = OSF.OsfControlTarget.TaskPane;
            break;
          case "2":
            targetType = OSF.OsfControlTarget.InContent;
            break;
          case "3":
            targetType = OSF.OsfControlTarget.Contextual;
            break;
          case "4":
            targetType = OSF.OsfControlTarget.TaskPane;
            break;
          default:
            // Unknown or unsupported targetType
            return false;
        }
        if (!this._checkControlTarget(targetType)) {
          return false;
        }
        // Neither requirements nor hosts are mentioned by agave.
        // It could either be a 1.0 agave, or wants to activate everywhere.
        // We'll trust OMEX in this case, and if appvers is in our list, return true.
        if (!entitlement.requirements && !entitlement.hosts) {
          // If there's no appvers list we trust the provider to only have returned relevent apps.
          if (!entitlement.hasOwnProperty("appVersions") || entitlement.appVersions === undefined) {
            return true;
          }
          return this._checkOmexAppVersions(entitlement.appVersions);
        }
        // Parse requirements and hosts
        // @ts-expect-error TS2554
        var pseudoParser = new OSF.Manifest.Manifest(function () {});
        var requirements, requirementsNode, hosts, hostsNode;
        if (entitlement.requirements) {
          pseudoParser._xmlProcessor = new OSF.XmlProcessor(entitlement.requirements, OSF.ManifestNamespaces["1.1"]);
          requirementsNode = pseudoParser._xmlProcessor.getDocumentElement();
        }
        requirements = pseudoParser._parseRequirements(requirementsNode);
        if (entitlement.hosts) {
          pseudoParser._xmlProcessor = new OSF.XmlProcessor(entitlement.hosts, OSF.ManifestNamespaces["1.1"]);
          hostsNode = pseudoParser._xmlProcessor.getDocumentElement();
        }
        hosts = pseudoParser._parseHosts(hostsNode);
        return (
          this._checkHosts(hosts) && this._checkSets(requirements.sets) && this._checkMethods(requirements.methods)
        );
      } catch (ex) {
        Telemetry.RuntimeTelemetryHelper.LogExceptionTag(
          "Error for checking is entitlement from omex supported or not",
          ex,
          null,
          0x1e361348 /* tag_4n7ni */,
        );
        return false;
      }
    }
    isEntitlementFromCorpCatalogSupported(entitlement) {
      if (!this.isFilteringEnabled()) {
        return true;
      }
      if (!entitlement) {
        return false;
      }
      var targetType = OSF.OfficeAppType[entitlement.OEType];
      if (!this._checkControlTarget(targetType)) {
        return false;
      }
      // @ts-expect-error TS2554
      var pseudoParser = new OSF.Manifest.Manifest(function () {});
      var hosts, sets, methods;
      if (entitlement.OfficeExtensionCapabilitiesXML) {
        pseudoParser._xmlProcessor = new OSF.XmlProcessor(
          entitlement.OfficeExtensionCapabilitiesXML,
          OSF.ManifestNamespaces["1.1"],
        );
        var xmlNode, requirements;
        xmlNode = pseudoParser._xmlProcessor.getDocumentElement();
        requirements = pseudoParser._parseRequirements(xmlNode);
        sets = requirements.sets;
        methods = requirements.methods;
        hosts = pseudoParser._parseHosts(xmlNode);
      }
      return this._checkHosts(hosts) && this._checkSets(sets) && this._checkMethods(methods);
    }
    setCapabilities(capabilities) {
      this._supportedCapabilities = this._scalarArrayToObject(capabilities);
    }
    setHosts(hosts) {
      this._supportedHosts = this._scalarArrayToObject(hosts);
    }
    setRequirements(requirements) {
      this._supportedSets = (requirements && this._arrayToSetsObject(requirements.sets)) || {};
      this._supportedMethods = (requirements && this._scalarArrayToObject(requirements.methods)) || {};
    }
    setSupportedControlTargets(controlTargets) {
      this._supportedControlTargets = this._scalarArrayToObject(controlTargets);
    }
    setSupportedOmexAppVersions(appVersions) {
      this._supportedOmexAppVersions = appVersions && appVersions.slice ? appVersions.slice(0) : [];
    }
    setFilteringEnabled(filteringEnabled) {
      this._filteringEnabled = filteringEnabled ? true : false;
    }
    isFilteringEnabled() {
      return this._filteringEnabled;
    }
    _checkManifest1_0(manifest) {
      return this._checkCapabilities(manifest.getCapabilities());
    }
    _checkCapabilities(askedCapabilities) {
      if (!askedCapabilities || askedCapabilities.length === 0) {
        return true;
      }
      for (var i = 0; i < askedCapabilities.length; i++) {
        if (this._supportedCapabilities[askedCapabilities[i]]) {
          return true;
        }
      }
      return false;
    }
    _checkManifest1_1(manifest) {
      var askedRequirements = manifest.getRequirements() || {};
      return (
        this._checkHosts(manifest.getHosts()) &&
        this._checkSets(askedRequirements.sets) &&
        this._checkMethods(askedRequirements.methods)
      );
    }
    _checkHosts(askedHosts) {
      if (!askedHosts || askedHosts.length === 0) {
        return true;
      }
      for (var i = 0; i < askedHosts.length; i++) {
        if (this._supportedHosts[askedHosts[i]]) {
          return true;
        }
      }
      return false;
    }
    _checkSets(askedSets) {
      if (!askedSets || askedSets.length === 0) {
        return true;
      }
      for (var i = 0; i < askedSets.length; i++) {
        var askedSet = askedSets[i];
        var supportedSet = this._supportedSets[askedSet.name.toLowerCase()];
        if (!supportedSet) {
          return false;
        }
        if (askedSet.version) {
          if (
            this._compareVersionStrings(supportedSet.minVersion || this.defaultMinMaxVersion, askedSet.version) > 0 ||
            this._compareVersionStrings(supportedSet.maxVersion || this.defaultMinMaxVersion, askedSet.version) < 0
          ) {
            return false;
          }
        }
      }
      return true;
    }
    _checkSetsForMos(askedSets) {
      if (!askedSets || askedSets.length === 0) {
        return true;
      }
      for (var i = 0; i < askedSets.length; i++) {
        var askedSet = askedSets[i];
        var supportedSet = this._supportedSets[askedSet.name?.toLowerCase()];
        if (!supportedSet) {
          return false;
        }
        if (askedSet.minVersion) {
          if (
            this._compareVersionStrings(askedSet.minVersion, supportedSet.minVersion || this.defaultMinMaxVersion) < 0 ||
            this._compareVersionStrings(askedSet.minVersion, supportedSet.maxVersion || this.defaultMinMaxVersion) > 0
          ) {
            return false;
          }
        }
        if (askedSet.maxVersion) {
          if (
            this._compareVersionStrings(askedSet.maxVersion, supportedSet.minVersion || this.defaultMinMaxVersion) < 0 ||
            this._compareVersionStrings(askedSet.maxVersion, supportedSet.maxVersion || this.defaultMinMaxVersion) > 0
          ) {
            return false;
          }
        }
      }
      return true;
    }
    _checkMethods(askedMethods) {
      if (!askedMethods || askedMethods.length === 0) {
        return true;
      }
      for (var i = 0; i < askedMethods.length; i++) {
        if (!this._supportedMethods[askedMethods[i]]) {
          return false;
        }
      }
      return true;
    }
    _checkControlTarget(askedControlTarget) {
      // If we can't parse app type (askedAppType is null/undefined), we can't allow insertion
      return askedControlTarget != undefined && this._supportedControlTargets[askedControlTarget];
    }
    _checkOmexAppVersions(askedAppVersions) {
      if (!askedAppVersions) {
        return false;
      }
      for (var i = 0; i < this._supportedOmexAppVersions.length; i++) {
        if (askedAppVersions.indexOf(this._supportedOmexAppVersions[i]) >= 0) {
          return true;
        }
      }
      return false;
    }
    _scalarArrayToObject(array) {
      var obj = {};
      if (array && array.length) {
        for (var i = 0; i < array.length; i++) {
          if (array[i] != undefined) {
            obj[array[i]] = true;
          }
        }
      }
      return obj;
    }
    _arrayToSetsObject(array) {
      var obj = {};
      if (array && array.length) {
        for (var i = 0; i < array.length; i++) {
          var set = array[i];
          if (set && set.name != undefined) {
            obj[set.name.toLowerCase()] = set; // toLower so we do case insensitive check
          }
        }
      }
      return obj;
    }
    _getSupportedSet() {
      var obj = {};
      var supportSetNames = Object.getOwnPropertyNames(this._supportedSets);
      for (var i = 0; i < supportSetNames.length; i++) {
        var supportedSet = this._supportedSets[supportSetNames[i]];
        obj[supportedSet.name.toLowerCase()] = supportedSet.maxVersion || this.defaultMinMaxVersion;
      }
      if (typeof JSON !== "undefined") {
        return JSON.stringify(obj);
      }
      return obj;
    }
    _getSupportedSetAsCapabilities() {
      return Object.getOwnPropertyNames(this._supportedSets).map((name) => {
        const supportedSet = this._supportedSets[name];
        return { Name: supportedSet.name.toLowerCase(), Version: supportedSet.maxVersion || this.defaultMinMaxVersion };
      });
    }
    _compareVersionStrings(leftVersion, rightVersion) {
      leftVersion = leftVersion.split(".");
      rightVersion = rightVersion.split(".");
      var maxComponentCount = Math.max(leftVersion.length, rightVersion.length);
      for (var i = 0; i < maxComponentCount; i++) {
        var leftInt = parseInt(leftVersion[i], 10) || 0,
          rightInt = parseInt(rightVersion[i], 10) || 0;
        if (leftInt === rightInt) {
          continue;
        }
        return leftInt - rightInt;
      }
      return 0;
    }
  }
}
